/**
 * Organization Users Tab
 *
 * List and manage users belonging to an organization.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { logAdminAction, ACTION_DESCRIPTIONS } from '@/lib/admin/auditLog';
import { AdminCard } from '@/components/admin/AdminShell';
import { InviteOrgUserModal } from '@/components/admin/InviteOrgUserModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import {
  UserPlus,
  MoreVertical,
  UserX,
  UserCheck,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { OrgUser } from '@/integrations/firebase/types';

interface OrgUsersTabProps {
  orgId: string;
  orgName: string;
}

export function OrgUsersTab({ orgId, orgName }: OrgUsersTabProps) {
  const { user } = useAuth();
  const { adminUser } = useAdminAuth();
  const { toast } = useToast();

  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);

        // Query users with this orgId
        const usersQuery = query(
          collection(db, 'org_users'),
          where('orgId', '==', orgId)
        );
        const snapshot = await getDocs(usersQuery);

        const usersData = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as OrgUser[];

        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [orgId]);

  const handleToggleStatus = async (targetUser: OrgUser) => {
    if (!user?.uid || !adminUser) return;

    const newStatus = targetUser.status === 'active' ? 'disabled' : 'active';

    try {
      await updateDoc(doc(db, 'org_users', targetUser.uid), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });

      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: newStatus === 'disabled' ? 'DISABLE_USER' : 'ENABLE_USER',
        targetOrgId: orgId,
        targetUserId: targetUser.uid,
        description:
          newStatus === 'disabled'
            ? ACTION_DESCRIPTIONS.DISABLE_USER(targetUser.email)
            : ACTION_DESCRIPTIONS.ENABLE_USER(targetUser.email),
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.uid === targetUser.uid ? { ...u, status: newStatus } : u
        )
      );

      toast({
        title: newStatus === 'disabled' ? 'User Disabled' : 'User Enabled',
        description: `${targetUser.email} is now ${newStatus}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleResendInvite = async (targetUser: OrgUser) => {
    if (!user?.uid || !adminUser) return;

    try {
      // Call the adminInviteUser function which handles resending for existing pending users
      const adminInviteUser = httpsCallable(functions, 'adminInviteUser');
      await adminInviteUser({
        orgId,
        email: targetUser.email,
        firstName: targetUser.firstName || '',
        lastName: targetUser.lastName || '',
        role: targetUser.role,
      });

      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: 'RESEND_INVITE',
        targetOrgId: orgId,
        targetUserId: targetUser.uid,
        description: ACTION_DESCRIPTIONS.RESEND_INVITE(targetUser.email),
      });

      toast({
        title: 'Invite Resent',
        description: `Invitation email sent to ${targetUser.email}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend invitation',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium text-white">Users</h2>
          <p className="text-sm text-gray-400">
            {users.length} user{users.length !== 1 ? 's' : ''} in this organization
          </p>
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      <AdminCard className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No users in this organization</p>
            <Button
              variant="link"
              onClick={() => setShowInviteModal(true)}
              className="text-[hsl(220,70%,60%)] mt-2"
            >
              Invite the first user
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(220,15%,20%)] hover:bg-transparent">
                <TableHead className="text-gray-400">User</TableHead>
                <TableHead className="text-gray-400">Role</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Last Login</TableHead>
                <TableHead className="text-gray-400 w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((orgUser) => (
                <TableRow
                  key={orgUser.uid}
                  className="border-[hsl(220,15%,20%)] hover:bg-[hsl(220,15%,12%)]"
                >
                  <TableCell>
                    <div>
                      <p className="font-medium text-white">
                        {orgUser.displayName || orgUser.email}
                      </p>
                      {orgUser.displayName && (
                        <p className="text-xs text-gray-500">{orgUser.email}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="border-[hsl(220,15%,30%)] text-gray-300 capitalize"
                    >
                      {orgUser.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        orgUser.status === 'active'
                          ? 'border-green-500/50 text-green-400'
                          : orgUser.status === 'pending'
                          ? 'border-yellow-500/50 text-yellow-400'
                          : 'border-red-500/50 text-red-400'
                      }
                    >
                      {orgUser.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    {formatDate(orgUser.lastLoginAt)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-white hover:bg-[hsl(220,15%,20%)]"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]"
                      >
                        {orgUser.status === 'pending' && (
                          <DropdownMenuItem
                            onClick={() => handleResendInvite(orgUser)}
                            className="text-gray-300 focus:text-white focus:bg-[hsl(220,15%,20%)]"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Resend Invite
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleToggleStatus(orgUser)}
                          className="text-gray-300 focus:text-white focus:bg-[hsl(220,15%,20%)]"
                        >
                          {orgUser.status === 'active' ? (
                            <>
                              <UserX className="h-4 w-4 mr-2" />
                              Disable User
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-2" />
                              Enable User
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminCard>

      <InviteOrgUserModal
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
        orgId={orgId}
        orgName={orgName}
        onSuccess={() => {
          setShowInviteModal(false);
          // Refresh users list
          window.location.reload();
        }}
      />
    </div>
  );
}
